import { EyeOff, Database, Clock, UserCheck, Link2, Ruler, Gauge, Lock, RefreshCw, TrendingUp, Building2, Trees, ArrowRight } from "lucide-react";
import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";
import { EnginePipeline, AgentBlocks } from "@/components/site/LandEngine";
import { Eyebrow, PillButton, Reveal } from "@/components/site/Bits";

const PROBLEM = [
    [EyeOff, "Hidden", "Listings hide exact locations. The best plots are buried among thousands of farms and ruins."],
    [Database, "Scattered", "The truth about a plot sits in a dozen places: municipal master plans (PDM), ecological and agricultural reserves, flood maps, grid data, price records."],
    [Clock, "Slow", "A human analyst needs days per plot. By then, a good plot is gone."],
];
const LAYERS = [
    ["Market", "Listings, asking prices, €/ha and €/m² benchmarks", "bg-[#D9CBA8]"],
    ["Planning", "PDM categories, building indices, approvals — PIP, alvará, loteamento", "bg-sage"],
    ["Constraints", "RAN, REN, Natura 2000, flood zones, protected trees, line corridors", "bg-[#C08A6A]"],
    ["Infrastructure", "220/400 kV substations, grid capacity, fibre, roads, water", "bg-[#9CC3C9]"],
    ["Demand", "Housing prices, population, data-centre and logistics activity, active developers", "bg-[#7E9BB8]"],
];
const TIMELINE = [
    ["06:00", "Scout flags a 40 ha plot priced well below area norms, close to a 400 kV substation."],
    ["06:20", "Analyst picks the data-centre track and starts research."],
    ["07:10", "Memo ready: planning status, constraints, grid evidence, comparables, score, verdict “Pursue”."],
    ["09:00", "A partner reviews it over coffee. Deal Desk drafts the approach."],
    ["09:30", "The partner approves. The email is sent."],
];
const PRINCIPLES = [
    [UserCheck, "Humans decide", "Agents prepare; partners approve every offer and message."],
    [Link2, "Sources or silence", "Every fact is linked. Nothing is invented."],
    [Ruler, "Fixed rubrics", "Every plot is scored the same way, so we compare like with like."],
    [Gauge, "Honest uncertainty", "Approximate locations and estimates are labelled as such."],
    [Lock, "Confidential by design", "Our scores, valuations and research never leave the firm."],
    [RefreshCw, "Always improving", "Every deal we win or lose feeds back into the briefs."],
];
const FOR_YOU = [
    [TrendingUp, "Investors", "More deals screened, stricter selection, faster execution — and a documented decision trail for every acquisition."],
    [Building2, "Developers & partners", "Land that arrives pre-studied: planning, constraints and grid already mapped."],
    [Trees, "Landowners & agents", "A buyer who already understands your land, asks precise questions and moves quickly."],
];

function H2({ children, dark }) {
    return <h2 className={`font-serif text-4xl sm:text-5xl leading-[1.04] tracking-tight mt-8 max-w-3xl ${dark ? "text-cream" : "text-forest"}`}>{children}</h2>;
}

function Tiles({ items, cols = "md:grid-cols-3", testid }) {
    return (
        <div className={`grid sm:grid-cols-2 ${cols} gap-px bg-hairline rounded-2xl overflow-hidden mt-14`} data-testid={testid}>
            {items.map(([Icon, t, b], i) => (
                <Reveal key={t} delay={i * 70} className="bg-cream p-7 md:p-8">
                    <Icon className="h-6 w-6 text-moss mb-6" strokeWidth={1.5} />
                    <p className="font-serif text-2xl text-forest mb-3">{t}</p>
                    <p className="text-sm text-muted_text leading-relaxed">{b}</p>
                </Reveal>
            ))}
        </div>
    );
}

const LD = { "@context": "https://schema.org", "@type": "WebPage", name: "Land Intelligence — how Verde 2050 uses AI", about: { "@id": "https://verde2050.com/#organization" },
    description: "How Verde 2050's AI agents screen, study and prepare land deals in Portugal — with people approving every decision." };

export default function LandIntelligence() {
    const toEngine = (e) => { e.preventDefault(); document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" }); };
    return (
        <div data-testid="land-intelligence-page" className="bg-cream text-ink min-h-screen">
            <SEO path="/land-intelligence" og="land-intelligence" title="AI for Land Investment in Portugal | Verde 2050"
                 description="Verde 2050's AI agents screen large land listings in Portugal daily and prepare each deal. People approve every offer, email and euro." jsonLd={LD} />
            <GroupHeader />

            <section data-testid="li-hero" className="relative isolate min-h-[92svh] flex items-end overflow-hidden bg-forest text-cream pb-16 md:pb-24 pt-32">
                <img src="/li/hero.webp" alt="Aerial view of the Lisbon region at dawn with glowing parcel outlines traced over the land" className="absolute inset-0 -z-10 h-full w-full object-cover hero-zoom" fetchpriority="high" />
                <div className="absolute inset-0 -z-10 bg-gradient-to-r from-forest/95 via-forest/60 to-forest/10" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
                    <Reveal><Eyebrow dark>Land Intelligence</Eyebrow></Reveal>
                    <Reveal delay={120}><h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight mt-8 max-w-4xl">Land, read by AI.<br /><em className="italic text-sage">Decided by people.</em></h1></Reveal>
                    <Reveal delay={240}><p className="mt-8 max-w-2xl text-base md:text-lg text-cream/85 leading-relaxed">We run a team of AI agents that screen every large land listing in Portugal daily, study each opportunity like an analyst, and prepare the deal — so our people spend their time only on land worth buying.</p></Reveal>
                    <Reveal delay={320} className="mt-10 flex flex-col sm:flex-row gap-3">
                        <a href="#how-it-works" onClick={toEngine} data-testid="li-cta-how" className="group inline-flex items-center justify-center gap-3 rounded-full bg-cream text-forest hover:bg-sage px-7 py-4 text-sm font-medium transition-colors">See how it works <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></a>
                        <PillButton to="/contact?type=land-intelligence" variant="ghost" testid="li-cta-talk">Use our AI for your business</PillButton>
                    </Reveal>
                </div>
            </section>

            <section data-testid="li-problem" className="bg-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <Reveal><Eyebrow>The problem</Eyebrow></Reveal>
                        <Reveal delay={100}><H2>Land is the most opaque <em className="italic">asset class.</em></H2></Reveal>
                    </div>
                    <Reveal className="lg:col-span-5 overflow-hidden rounded-2xl aspect-[3/2]"><img src="/li/problem.webp" alt="A desk covered in paper maps, planning sheets and printouts" loading="lazy" className="h-full w-full object-cover" /></Reveal>
                </div>
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Tiles items={PROBLEM} testid="li-problem-tiles" />
                    <Reveal><p className="mt-12 font-serif text-3xl text-forest">So we built a system that reads all of it, <em className="italic">every day.</em></p></Reveal>
                </div>
            </section>

            <EnginePipeline />
            <AgentBlocks />

            <section data-testid="li-layers" className="bg-forest text-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 items-center">
                    <Reveal className="lg:col-span-6 overflow-hidden rounded-[1.75rem] aspect-square"><img src="/li/layers.webp" alt="Five translucent map layers stacked above a 3D terrain model of a hillside plot" loading="lazy" className="h-full w-full object-cover" /></Reveal>
                    <div className="lg:col-span-6">
                        <Reveal><Eyebrow dark>Five layers of land intelligence</Eyebrow></Reveal>
                        <Reveal delay={100}><H2 dark>One plot. <em className="italic text-sage">Every layer that matters.</em></H2></Reveal>
                        <ol className="mt-10 space-y-px rounded-2xl overflow-hidden bg-cream/10">
                            {LAYERS.map(([t, b, c], i) => (
                                <Reveal as="li" key={t} delay={i * 80} className="flex gap-5 bg-forest p-5" data-testid={`li-layer-${t.toLowerCase()}`}>
                                    <span className={`mt-1.5 h-3 w-3 shrink-0 rounded-sm ${c}`} />
                                    <div><p className="font-medium text-cream">{t}</p><p className="text-sm text-cream/70 leading-relaxed mt-1">{b}</p></div>
                                </Reveal>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>

            <section data-testid="li-timeline" className="bg-cream py-24 md:py-32">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <Reveal><Eyebrow>From listing to memo</Eyebrow></Reveal>
                    <Reveal delay={100}><H2>One gem, <em className="italic">end to end.</em></H2></Reveal>
                    <p className="mt-4 text-xs text-muted_text">Illustrative example based on how the system works. Location and price withheld.</p>
                    <ol className="mt-12 relative border-l border-hairline ml-3">
                        {TIMELINE.map(([time, text], i) => (
                            <Reveal as="li" key={time} delay={i * 90} className="relative pl-10 pb-10 last:pb-0">
                                <span className={`absolute -left-[7px] top-1.5 h-3.5 w-3.5 rounded-full ring-4 ring-cream ${i === TIMELINE.length - 1 ? "bg-forest" : "bg-sage"}`} />
                                <p className="num-mono text-moss text-sm">{time}</p>
                                <p className="mt-1 text-lg text-ink leading-relaxed">{text}</p>
                            </Reveal>
                        ))}
                    </ol>
                    <Reveal><p className="mt-12 font-serif text-3xl text-forest leading-tight">A traditional team does this in a week. We do it before breakfast — <em className="italic">and a human still makes the call.</em></p></Reveal>
                </div>
            </section>

            <section data-testid="li-principles" className="bg-surface py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal><Eyebrow>How we operate</Eyebrow></Reveal>
                    <Reveal delay={100}><H2>Disciplined, <em className="italic">not magical.</em></H2></Reveal>
                    <Tiles items={PRINCIPLES} testid="li-principle-tiles" />
                </div>
            </section>

            <section data-testid="li-for-you" className="bg-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal><Eyebrow>What this means for you</Eyebrow></Reveal>
                    <Reveal delay={100}><H2>Better land, <em className="italic">studied before it reaches you.</em></H2></Reveal>
                    <Tiles items={FOR_YOU} testid="li-for-you-tiles" />
                </div>
            </section>

            <section data-testid="li-developers" className="bg-surface py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <Reveal><Eyebrow>For developers</Eyebrow></Reveal>
                        <Reveal delay={100}><H2>Bring Land Intelligence <em className="italic">into your business.</em></H2></Reveal>
                        <Reveal delay={200} className="mt-8 space-y-5 text-base md:text-lg text-muted_text leading-relaxed max-w-2xl">
                            <p>The framework on this page is not a concept — we built it for our own projects and use it every day. Now we help developers implement it across their business.</p>
                            <p>We map your markets, sourcing and decision process, then set up the agents, data layers and human checkpoints around your team. Your people stay in charge of every decision; the technology does the groundwork.</p>
                        </Reveal>
                    </div>
                    <Reveal delay={300} className="lg:col-span-5 flex flex-col gap-4 lg:items-end">
                        <PillButton to="/contact?type=land-intelligence" testid="li-cta-developers">Implement our AI framework</PillButton>
                        <p className="text-sm text-muted_text lg:text-right max-w-xs">Tell us about your business and we'll come back to you to explore the fit.</p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="li-closing" className="bg-forest text-cream py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal><H2 dark>Have land, capital or a project? <em className="italic text-sage">Let's talk.</em></H2></Reveal>
                    <Reveal delay={150} className="mt-12 flex flex-col sm:flex-row gap-3">
                        <PillButton to="/sell-your-land" variant="light" testid="li-cta-sell">Sell us land</PillButton>
                        <PillButton to="/contact?type=invest" variant="ghost" testid="li-cta-invest">Invest with us</PillButton>
                        <PillButton to="/contact?type=project" variant="ghost" testid="li-cta-partner">Partner with us</PillButton>
                    </Reveal>
                </div>
            </section>
            <GroupFooter />
        </div>
    );
}
